/**
 * Mission & Values Page — CHI Alliance POC
 * Visual direction: people-first editorial imagery sits beneath a composed, high-contrast Mission statement.
 * Typography: the editorial layout uses Space Grotesk hierarchy with readable DM Sans/Inter support copy.
 * Interaction: Core Values reuse the home page's fundamentals experience, fed this page's value copy.
 */
import Navigation from "@/components/Navigation";
import SectionBadge from "@/components/SectionBadge";
import Footer from "@/components/Footer";
import MembershipBanner from "@/components/MembershipBanner";
import { Users } from "lucide-react";
import { FadeUp } from "@/components/ScrollAnimations";
import CTADrawer from "@/components/CTADrawer";
import MissionLotusMark from "@/components/MissionLotusMark";
import CtaIcon from "@/components/CtaIcon";
import HomeFundamentalsExperience, { type FundamentalStory } from "@/components/HomeFundamentalsExperience";
import { useState } from "react";

type CoreValueStory = {
  id: "connect" | "learn" | "grow";
  number: string;
  title: string;
  color: string;
  image: string;
  imagePosition: string;
  eyebrow: string;
  headline: string;
  body: string;
  detail: string;
};

const CORE_VALUE_STORIES: readonly CoreValueStory[] = [
  {
    id: "connect",
    number: "01",
    title: "Connect",
    color: "#E91E8C",
    image: "/media/speakers/harry-on-stage.webp",
    imagePosition: "center 30%",
    eyebrow: "Connect",
    headline: "Authentic relationships make lasting progress possible.",
    body: "We create welcoming rooms where people can meet with intention, earn trust over time, and build relationships that extend far beyond one conversation.",
    detail: "Trust turns a gathering into a community with real staying power.",
  },
  {
    id: "learn",
    number: "02",
    title: "Learn",
    color: "#F5A623",
    image: "/media/backgrounds/event-detail-community.webp",
    imagePosition: "center 42%",
    eyebrow: "Learn & Collaborate",
    headline: "Shared experience gives every idea more momentum.",
    body: "We exchange hard-won perspective, ask better questions, and collaborate across disciplines so insight can move from conversation into meaningful action.",
    detail: "Learning together creates the clarity and confidence to act together.",
  },
  {
    id: "grow",
    number: "03",
    title: "Grow",
    color: "#4AB857",
    image: "/media/backgrounds/home-hero-community.webp",
    imagePosition: "center 46%",
    eyebrow: "Grow",
    headline: "Collective growth makes individual potential go further.",
    body: "We champion thoughtful leadership, professional momentum, and meaningful purpose—because a stronger future is built when people elevate themselves and one another.",
    detail: "Progress has more purpose when the whole community rises with it.",
  },
];

/**
 * The Core Values copy in the shape the shared fundamentals experience expects.
 * Same three pillars as the home page, told with this page's imagery and language.
 */
const CORE_VALUE_FUNDAMENTALS: readonly FundamentalStory[] = CORE_VALUE_STORIES.map(value => ({
  id: value.id,
  number: value.number,
  label: value.title,
  eyebrow: value.eyebrow,
  title: value.headline,
  body: value.body,
  detail: value.detail,
  accent: value.color,
  background: value.image,
  backgroundPosition: value.imagePosition,
}));

export default function MissionValues() {
  const [joinDrawerOpen, setJoinDrawerOpen] = useState(false);

  return (
    <div style={{ background: "#080C18", minHeight: "100vh" }}>
      <Navigation />

      <style>{`
        /*
          A full screen. This used to be one screen *minus* the partner band, to open
          at the same scale as the Home hero; it is now the whole viewport, so the
          Mission hero fills the window on load and the partner band sits below the
          fold rather than being reserved for.

          Both units on purpose: svh is the small-viewport height, which on mobile
          excludes the browser chrome that appears and disappears as you scroll. Plain
          vh there measures the *largest* viewport, so a 100vh hero is taller than the
          visible window and its content sits partly under the address bar. The vh
          line is the fallback for anything that does not know svh.
        */
        .mission-hero {
          display: flex;
          flex-direction: column;
          justify-content: center;
          min-height: 100vh;
          min-height: 100svh;
        }

	        @media (min-width: 1024px) {
          .mission-hero__background {
            /* Anchor the left conversation pair in-frame instead of treating it as an edge crop. */
            background-position: 38% 5% !important;
	          }

          .mission-hero__statement {
            padding-bottom: clamp(2.75rem, 4.5vw, 4.25rem) !important;
          }
        }

	        @media (max-width: 1023px) {
	          .mission-hero__background {
            /* Preserve the upper-left conversation subject in a narrow crop. */
            background-position: 24% 7% !important;
            background-size: auto 112% !important;
          }

	          .mission-hero {
	            /* Match the desktop story offset: the heading and statement begin 25px lower. */
	            padding-top: 129px !important;
	          }

          .mission-hero__statement > div {
            padding: clamp(1.35rem, 6vw, 2rem) !important;
            border-radius: 1.2rem !important;
          }

          .mission-values-section {
            padding: 2.5rem 0 3.5rem !important;
          }

          .mission-values-heading {
            margin-bottom: 2rem !important;
          }
        }
      `}</style>

      {/* ── HERO + MISSION — people-first editorial background, fades into Core Values ── */}
      <section
        className="mission-hero"
        style={{
          // Keep the heading and mission statement 25px lower from the hero top.
          paddingTop: "153px",
          paddingBottom: "0",
          position: "relative",
          overflow: "hidden",
          textAlign: "center",
        }}
      >
        {/* People-centered editorial background with a calm text-safe center */}
        <div
          className="mission-hero__background"
          style={{
            position: "absolute",
	            inset: 0,
	            backgroundImage: `url('/media/backgrounds/mission-human-connection.webp')`,
	            backgroundSize: "cover",
	            // Start from the upper portion of the photo so the conversation is not cropped at the hairline.
	            backgroundPosition: "center 5%",
	            // Matches the Home hero image weight; the overlay stack above is tuned for 0.45.
	            opacity: 0.45,
	          }}
        />
        {/* Same overlay stack as the Home hero — flat scrim, vertical fade, edge vignette, brand washes —
            with the bottom stop landing on #0D1220 so the hero still dissolves into Core Values. */}
        <div
          className="mission-hero__gradient"
          style={{
            position: "absolute",
            inset: 0,
            background: `
              radial-gradient(ellipse at 15% 60%, rgba(233,30,140,0.18) 0%, transparent 50%),
              radial-gradient(ellipse at 85% 25%, rgba(0,212,255,0.10) 0%, transparent 45%),
              linear-gradient(90deg,
                rgba(8,12,24,0.70) 0%,
                transparent 25%,
                transparent 75%,
                rgba(8,12,24,0.70) 100%
              ),
              linear-gradient(180deg,
                rgba(8,12,24,0.72) 0%,
                rgba(8,12,24,0.20) 30%,
                rgba(8,12,24,0.15) 55%,
                rgba(8,12,24,0.82) 88%,
                #0D1220 100%
              ),
              linear-gradient(rgba(8,12,24,0.45), rgba(8,12,24,0.45))
            `,
            zIndex: 1,
          }}
        />

        {/* Hero content */}
        <div className="container relative" style={{ zIndex: 10, paddingBottom: "2.5rem" }}>
          <MissionLotusMark size={220} />
          <h1
            style={{
              fontFamily: "'Bebas Neue', sans-serif",
              fontWeight: 800,
              fontSize: "clamp(2.25rem, 5vw, 4.5rem)",
              lineHeight: 1.08,
              letterSpacing: "-0.02em",
              color: "white",
              marginBottom: "1.5rem",
              textShadow: "none",
            }}
          >
            Our{" "}
            <span style={{ background: "linear-gradient(135deg, #E91E8C, #F5A623)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>
              Mission
            </span>
          </h1>
        </div>

        {/* Mission Statement card — inside same background section */}
        <div className="mission-hero__statement container relative" style={{ zIndex: 10, paddingBottom: "4.5rem" }}>
          <div
            style={{
              maxWidth: "860px",
              margin: "0 auto",
              background: "rgba(8,12,24,0.68)",
              backdropFilter: "blur(28px)",
              WebkitBackdropFilter: "blur(28px)",
              border: "1px solid rgba(233,30,140,0.28)",
              borderRadius: "1.5rem",
              padding: "3.5rem",
              position: "relative",
              overflow: "hidden",
              boxShadow: "0 32px 80px rgba(0,0,0,0.6)",
            }}
          >
            <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: "3px", background: "linear-gradient(90deg, #E91E8C, #F5A623, #00D4FF)" }} />
            <p
              style={{
                fontFamily: "'DM Sans', 'Inter', sans-serif",
                fontWeight: 500,
                fontSize: "clamp(1.1rem, 2vw, 1.35rem)",
                color: "rgba(255,255,255,0.9)",
                lineHeight: 1.65,
              }}
            >
              The CHI Alliance exists to create meaningful human connections that inspire learning, foster collaboration, and empower individuals and organizations to grow together.
            </p>
            <p style={{ color: "#CBD5E1", fontSize: "var(--chi-body)", lineHeight: 1.8, fontFamily: "'Inter', sans-serif", marginTop: "1.5rem" }}>
              We believe that when people share their experiences, ideas, and wisdom, they create stronger communities, better leaders, and greater opportunities for all.
            </p>
          </div>
        </div>
      </section>

      {/* ── CORE VALUES — the same rotating experience the home page uses, driven by the Core Values copy ── */}
      <section className="mission-values-section" style={{ background: "#090D1B", padding: 0 }}>
        <FadeUp duration={700}>
          <HomeFundamentalsExperience
            stories={CORE_VALUE_FUNDAMENTALS}
            introKicker="The principles behind every room"
            introHeading={<>Our Core <span>Values</span></>}
            ariaLabel="The CHI Alliance core values"
          />
        </FadeUp>
      </section>

      {/* ── GUIDING BELIEF + CONTACT FORM ── */}
      <section
        style={{
          position: "relative",
          minHeight: "600px",
          overflow: "hidden",
          display: "flex",
          alignItems: "stretch",
        }}
      >
        {/* Full-bleed faces mosaic background */}
        <div
          style={{
            position: "absolute",
            inset: 0,
            backgroundImage: `url('/media/backgrounds/faces-mosaic.webp')`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            opacity: 0.35,
          }}
        />
        {/* Dark overlay for legibility */}
        <div style={{ position: "absolute", inset: 0, background: "rgba(8,12,24,0.6)" }} />

        {/* Content grid */}
        <div
          className="container relative z-10 grid grid-cols-1 lg:grid-cols-2"
          style={{
            gap: "3rem",
            alignItems: "center",
            padding: "clamp(3rem, 6vw, 6rem) 2rem",
          }}
        >
          {/* Left — Guiding Belief */}
          <div>
            <SectionBadge>Our Guiding Belief</SectionBadge>
            <h2
              style={{
                fontFamily: "'Bebas Neue', sans-serif",
                fontWeight: 800,
                fontSize: "clamp(2rem, 3.5vw, 3rem)",
                color: "white",
                lineHeight: 1.15,
                marginBottom: "1.5rem",
              }}
            >
              Human connection is our greatest catalyst for innovation, opportunity, and transformation.
            </h2>
            <p style={{ color: "#CBD5E1", fontSize: "1.1rem", lineHeight: 1.8, fontFamily: "'Inter', sans-serif", marginBottom: "2rem" }}>
              Together, we connect, learn, collaborate, and grow.
            </p>
            <div style={{ height: 2, width: 80, background: "linear-gradient(90deg, #E91E8C, #F5A623)", borderRadius: 2 }} />
          </div>

          {/* Right — Contact Form */}
          <div
            style={{
              background: "rgba(8,12,24,0.82)",
              backdropFilter: "blur(20px)",
              WebkitBackdropFilter: "blur(20px)",
              border: "1px solid rgba(255,255,255,0.1)",
              borderRadius: "1.5rem",
              padding: "clamp(1.25rem, 5vw, 2.5rem)",
              boxShadow: "0 24px 64px rgba(0,0,0,0.5)",
            }}
          >
            <h3 style={{ fontFamily: "'Space Grotesk', sans-serif", fontWeight: 700, fontSize: "1.25rem", color: "white", marginBottom: "1.75rem" }}>
              Join the Community
            </h3>

            {/* Name row */}
            <div className="grid grid-cols-1 sm:grid-cols-2" style={{ gap: "1rem", marginBottom: "1.25rem" }}>
              <div>
                <label style={{ display: "block", color: "#94A3B8", fontSize: "0.8rem", fontFamily: "'Inter', sans-serif", marginBottom: "0.4rem" }}>
                  First <span style={{ color: "#E91E8C" }}>*</span>
                </label>
                <input
                  type="text"
                  placeholder=""
                  style={{
                    width: "100%",
                    background: "rgba(255,255,255,0.06)",
                    border: "1px solid rgba(255,255,255,0.12)",
                    borderRadius: "0.6rem",
                    padding: "0.65rem 0.875rem",
                    color: "white",
                    fontFamily: "'Inter', sans-serif",
                    fontSize: "0.9rem",
                    outline: "none",
                    transition: "border-color 0.2s",
                  }}
                  onFocus={e => (e.currentTarget.style.borderColor = "rgba(233,30,140,0.6)")}
                  onBlur={e => (e.currentTarget.style.borderColor = "rgba(255,255,255,0.12)")}
                />
              </div>
              <div>
                <label style={{ display: "block", color: "#94A3B8", fontSize: "0.8rem", fontFamily: "'Inter', sans-serif", marginBottom: "0.4rem" }}>
                  Last <span style={{ color: "#E91E8C" }}>*</span>
                </label>
                <input
                  type="text"
                  placeholder=""
                  style={{
                    width: "100%",
                    background: "rgba(255,255,255,0.06)",
                    border: "1px solid rgba(255,255,255,0.12)",
                    borderRadius: "0.6rem",
                    padding: "0.65rem 0.875rem",
                    color: "white",
                    fontFamily: "'Inter', sans-serif",
                    fontSize: "0.9rem",
                    outline: "none",
                    transition: "border-color 0.2s",
                  }}
                  onFocus={e => (e.currentTarget.style.borderColor = "rgba(233,30,140,0.6)")}
                  onBlur={e => (e.currentTarget.style.borderColor = "rgba(255,255,255,0.12)")}
                />
              </div>
            </div>

            {/* Company */}
            <div style={{ marginBottom: "1.25rem" }}>
              <label style={{ display: "block", color: "#94A3B8", fontSize: "0.8rem", fontFamily: "'Inter', sans-serif", marginBottom: "0.4rem" }}>
                Company / Organization <span style={{ color: "#E91E8C" }}>*</span>
              </label>
              <input
                type="text"
                style={{
                  width: "100%",
                  background: "rgba(255,255,255,0.06)",
                  border: "1px solid rgba(255,255,255,0.12)",
                  borderRadius: "0.6rem",
                  padding: "0.65rem 0.875rem",
                  color: "white",
                  fontFamily: "'Inter', sans-serif",
                  fontSize: "0.9rem",
                  outline: "none",
                  transition: "border-color 0.2s",
                }}
                onFocus={e => (e.currentTarget.style.borderColor = "rgba(233,30,140,0.6)")}
                onBlur={e => (e.currentTarget.style.borderColor = "rgba(255,255,255,0.12)")}
              />
            </div>

            {/* Phone */}
            <div style={{ marginBottom: "1.25rem" }}>
              <label style={{ display: "block", color: "#94A3B8", fontSize: "0.8rem", fontFamily: "'Inter', sans-serif", marginBottom: "0.4rem" }}>Phone</label>
              <input
                type="tel"
                style={{
                  width: "100%",
                  background: "rgba(255,255,255,0.06)",
                  border: "1px solid rgba(255,255,255,0.12)",
                  borderRadius: "0.6rem",
                  padding: "0.65rem 0.875rem",
                  color: "white",
                  fontFamily: "'Inter', sans-serif",
                  fontSize: "0.9rem",
                  outline: "none",
                  transition: "border-color 0.2s",
                }}
                onFocus={e => (e.currentTarget.style.borderColor = "rgba(233,30,140,0.6)")}
                onBlur={e => (e.currentTarget.style.borderColor = "rgba(255,255,255,0.12)")}
              />
            </div>

            {/* Email */}
            <div style={{ marginBottom: "1.25rem" }}>
              <label style={{ display: "block", color: "#94A3B8", fontSize: "0.8rem", fontFamily: "'Inter', sans-serif", marginBottom: "0.4rem" }}>
                Email <span style={{ color: "#E91E8C" }}>*</span>
              </label>
              <input
                type="email"
                style={{
                  width: "100%",
                  background: "rgba(255,255,255,0.06)",
                  border: "1px solid rgba(255,255,255,0.12)",
                  borderRadius: "0.6rem",
                  padding: "0.65rem 0.875rem",
                  color: "white",
                  fontFamily: "'Inter', sans-serif",
                  fontSize: "0.9rem",
                  outline: "none",
                  transition: "border-color 0.2s",
                }}
                onFocus={e => (e.currentTarget.style.borderColor = "rgba(233,30,140,0.6)")}
                onBlur={e => (e.currentTarget.style.borderColor = "rgba(255,255,255,0.12)")}
              />
            </div>

            {/* Interested In */}
            <div style={{ marginBottom: "1.75rem" }}>
              <label style={{ display: "block", color: "#94A3B8", fontSize: "0.8rem", fontFamily: "'Inter', sans-serif", marginBottom: "0.4rem" }}>
                What are you Interested in? <span style={{ color: "#E91E8C" }}>*</span>
              </label>
              <select
                style={{
                  width: "100%",
                  background: "rgba(255,255,255,0.06)",
                  border: "1px solid rgba(255,255,255,0.12)",
                  borderRadius: "0.6rem",
                  padding: "0.65rem 0.875rem",
                  color: "white",
                  fontFamily: "'Inter', sans-serif",
                  fontSize: "0.9rem",
                  outline: "none",
                  cursor: "pointer",
                  appearance: "none",
                }}
              >
                <option value="learning-networking" style={{ background: "#0D1220" }}>Learning &amp; Networking</option>
                <option value="speaking" style={{ background: "#0D1220" }}>Speaking Opportunities</option>
                <option value="sponsorship" style={{ background: "#0D1220" }}>Sponsorship / Partnership</option>
                <option value="membership" style={{ background: "#0D1220" }}>General Membership</option>
                <option value="all" style={{ background: "#0D1220" }}>All of the Above</option>
              </select>
            </div>

            {/* Submit */}
            <button
              style={{
                width: "100%",
                background: "linear-gradient(135deg, #E91E8C, #F5A623)",
                border: "none",
                borderRadius: "0.6rem",
                padding: "0.875rem 1.5rem",
                color: "white",
                fontFamily: "'Space Grotesk', sans-serif",
                fontWeight: 700,
                fontSize: "0.95rem",
                letterSpacing: "0.04em",
                cursor: "pointer",
                transition: "opacity 0.2s, transform 0.15s",
                boxShadow: "0 4px 20px rgba(233,30,140,0.35)",
              }}
              onMouseEnter={e => { e.currentTarget.style.opacity = "0.9"; e.currentTarget.style.transform = "translateY(-1px)"; }}
              onMouseLeave={e => { e.currentTarget.style.opacity = "1"; e.currentTarget.style.transform = "translateY(0)"; }}
              onClick={() => setJoinDrawerOpen(true)}
            >
              Join the Community <CtaIcon icon={Users} />
            </button>
          </div>
        </div>

        {/* Mobile stacked layout override */}
        <style>{`
          @media (max-width: 768px) {
            .belief-grid { grid-template-columns: 1fr !important; gap: 2rem !important; }
          }
        `}</style>
      </section>

      <MembershipBanner
        headingLead="Put the mission"
        headingAccent="to work."
        copy={"Full member bios, verified profile links, priority event registration, and the member badge — membership is free."}
      />

      <Footer />

      <CTADrawer open={joinDrawerOpen} onClose={() => setJoinDrawerOpen(false)} variant="join-community" />
    </div>
  );
}
